import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BlogNav from './components/BlogNav';
import BlogCard from './components/BlogCard';
import { mockArticles } from '../../mocks/blog';
import type { Article } from '../../mocks/blog';
import SeoJsonLd from '@/components/feature/SeoJsonLd';
import PageSeo from '@/components/feature/PageSeo';

const ALL_TAGS = ['全部', 'AI 虛擬人', 'IP 建構', '商業應用', '短視頻', '品牌行銷', '動態特效', '設計趨勢', '3D 動畫', 'CGI', '品牌策略', 'AI 技術', '影視動畫'];

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const blogListJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Blog',
  name: '四相AI影視學院 - 創作日誌',
  url: `${SITE_URL}/blog`,
  description: '分享 AI 影視、虛擬人 IP 與品牌視覺的創作思考',
  inLanguage: 'zh-TW',
  author: {
    '@type': 'Person',
    name: 'Adam Chen',
  },
  publisher: {
    '@type': 'Organization',
    name: '四相AI影視學院',
    url: SITE_URL,
  },
};

function getArticles(): Article[] {
  try {
    const stored = localStorage.getItem('blog_articles');
    if (stored) {
      const parsed: Article[] = JSON.parse(stored);
      const published = parsed.filter((a) => a.status === 'published');
      return published.length > 0 ? published : mockArticles;
    }
  } catch {
    // ignore
  }
  return mockArticles;
}

export default function BlogPage() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [activeTag, setActiveTag] = useState('全部');
  const [searchQuery, setSearchQuery] = useState('');
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setArticles(getArticles());
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  const allTags = ['全部', ...Array.from(new Set(articles.flatMap((a) => a.tags)))];

  const filtered = articles.filter((a) => {
    const matchTag = activeTag === '全部' || a.tags.includes(activeTag);
    const q = searchQuery.toLowerCase();
    const matchSearch = !q || a.title.toLowerCase().includes(q) || a.summary.toLowerCase().includes(q);
    return matchTag && matchSearch;
  });

  return (
    <div className="min-h-screen bg-black">
      <PageSeo
        title="創作日誌 - 四相AI影視學院｜AI影視、虛擬人IP與品牌視覺"
        description="四相AI影視學院創作日誌，分享AI影視、虛擬人IP建構、動態特效與品牌視覺的創作思考與技術洞察，探索AI影視最前沿趨勢。"
        keywords="AI影視部落格,虛擬人IP,動態特效教學,3D動畫,品牌視覺,四相AI影視學院"
        canonical={`${SITE_URL}/blog`}
        ogType="blog"
      />
      <SeoJsonLd data={blogListJsonLd} id="blog-list-jsonld" />
      <BlogNav />

      {/* Hero banner */}
      <section className="relative pt-32 pb-20 px-8 overflow-hidden">
        {/* Background glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-amber-500/5 blur-3xl rounded-full pointer-events-none" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" />

        <div
          className={`relative z-10 max-w-5xl mx-auto text-center transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-px bg-gradient-to-r from-transparent to-amber-400/60" />
            <span
              className="text-amber-400/70 text-xs tracking-[0.5em] uppercase"
              style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
            >
              Insights &amp; Stories
            </span>
            <div className="w-12 h-px bg-gradient-to-l from-transparent to-amber-400/60" />
          </div>

          <h1
            className="text-5xl md:text-6xl font-black mb-5 leading-tight"
            style={{
              fontFamily: "'Noto Serif TC', serif",
              background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 40%, #fde68a 70%, #d97706 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              filter: 'drop-shadow(0 0 30px rgba(251,191,36,0.3))',
            }}
          >
            創作日誌
          </h1>
          <p
            className="text-white/50 text-base tracking-widest"
            style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
          >
            分享 AI 影視、虛擬人 IP 與品牌視覺的創作思考
          </p>
        </div>
      </section>

      {/* Search + Filter */}
      <section className="px-8 pb-10 max-w-6xl mx-auto">
        {/* Search bar */}
        <div
          className={`relative mb-8 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
        >
          <div className="relative max-w-xl mx-auto">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-amber-500/50 text-base" />
            <input
              type="text"
              placeholder="搜尋文章標題或內容..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-5 py-3 bg-white/[0.04] border border-amber-500/15 rounded-sm text-white/80 placeholder-white/25 focus:outline-none focus:border-amber-400/40 transition-all duration-300 text-sm"
              style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
            />
          </div>
        </div>

        {/* Tag filter */}
        <div
          className={`flex flex-wrap gap-2 justify-center mb-12 transition-all duration-700 delay-150 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
        >
          {allTags.map((tag) => (
            <button
              key={tag}
              onClick={() => setActiveTag(tag)}
              className={`px-4 py-1.5 text-xs tracking-wider rounded-sm border transition-all duration-300 whitespace-nowrap cursor-pointer ${
                activeTag === tag
                  ? 'bg-amber-500/20 border-amber-400/60 text-amber-400 shadow-lg shadow-amber-500/10'
                  : 'bg-white/[0.03] border-white/10 text-white/40 hover:border-amber-500/30 hover:text-amber-400/70'
              }`}
              style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
            >
              {tag}
            </button>
          ))}
        </div>

        {/* Article count */}
        <div
          className={`flex items-center gap-3 mb-8 transition-all duration-700 delay-200 ${visible ? 'opacity-100' : 'opacity-0'}`}
        >
          <div className="w-6 h-px bg-amber-500/40" />
          <span
            className="text-white/30 text-xs tracking-[0.3em]"
            style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
          >
            共 {filtered.length} 篇文章
          </span>
        </div>

        {/* Grid */}
        {filtered.length > 0 ? (
          <div
            className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            {filtered.map((article, i) => (
              <BlogCard key={article.id} article={article} index={i} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-32 text-center">
            <i className="ri-file-search-line text-4xl text-amber-500/20 mb-4" />
            <p
              className="text-white/30 text-sm tracking-wider"
              style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
            >
              沒有找到符合條件的文章
            </p>
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="relative mt-20 px-8 py-12 border-t border-amber-500/10">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-8">
            <button
              onClick={() => navigate('/')}
              className="text-white/30 hover:text-amber-400 transition-colors duration-300 text-sm cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
            >
              回到首頁
            </button>
            <button
              onClick={() => navigate('/blog')}
              className="text-amber-400/60 text-sm cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
            >
              文章列表
            </button>
          </div>
          <p
            className="text-white/20 text-xs tracking-widest"
            style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
          >
            © 2025 Adam Chen. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
